import { createWithEqualityFn } from 'zustand/traditional';
import { getUniqueIds } from '@/utils/selection';

export type ActiveMemberAssignment = {
  badgeId: string | null;
  organizationId: string | null;
  personId: string | null;
  selectedPersonIds: string[];
  subjectLabel: string | null;
  teamId: string | null;
  testIDPrefix: string;
};

type MemberAssignmentStoreState = {
  activeAssignment: ActiveMemberAssignment | null;
};

const initialState: MemberAssignmentStoreState = {
  activeAssignment: null,
};

const useMemberAssignmentStore =
  createWithEqualityFn<MemberAssignmentStoreState>(() => initialState);

export default useMemberAssignmentStore;

export function openMemberAssignment({
  organizationId,
  personId,
  selectedPersonIds = [],
  subjectLabel,
  teamId,
  testIDPrefix = 'MemberAction',
}: {
  organizationId?: string | null;
  personId?: string | null;
  selectedPersonIds?: string[];
  subjectLabel?: string | null;
  teamId?: string | null;
  testIDPrefix?: string;
}) {
  const uniqueSelectedPersonIds = getUniqueIds(selectedPersonIds);

  useMemberAssignmentStore.setState({
    activeAssignment: {
      badgeId: null,
      organizationId: organizationId ?? null,
      personId: personId ?? null,
      selectedPersonIds: uniqueSelectedPersonIds,
      subjectLabel: subjectLabel ?? null,
      teamId: teamId ?? null,
      testIDPrefix,
    },
  });
}

export function setMemberAssignmentBadgeId(badgeId: string) {
  useMemberAssignmentStore.setState((state) => {
    const activeAssignment = state.activeAssignment;

    if (!activeAssignment) {
      return state;
    }

    return {
      activeAssignment: {
        ...activeAssignment,
        badgeId,
      },
    };
  });
}

export function getMemberAssignmentSelectedLabel(
  assignment: ActiveMemberAssignment | null,
) {
  if (!assignment) {
    return 'selected members';
  }

  if (assignment.subjectLabel) {
    return assignment.subjectLabel;
  }

  const selectedCount = getMemberAssignmentSelectedCount(assignment);

  return selectedCount === 1 ? '1 selected' : `${selectedCount} selected`;
}

export function getMemberAssignmentSelectedCount(
  assignment: ActiveMemberAssignment | null,
) {
  if (!assignment) {
    return 0;
  }

  return assignment.personId ? 1 : assignment.selectedPersonIds.length;
}

export function getMemberAssignmentSelectedParam(
  assignment: ActiveMemberAssignment | null,
) {
  if (!assignment?.selectedPersonIds.length) {
    return undefined;
  }

  return assignment.selectedPersonIds.join(',');
}

export function hasMemberAssignmentContext(
  assignment: ActiveMemberAssignment | null,
) {
  return Boolean(
    assignment &&
      (assignment.organizationId || assignment.teamId) &&
      getMemberAssignmentSelectedCount(assignment) > 0,
  );
}
